import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useMemo } from 'react';

import { fetchPage, fetchPagesForSelection, searchPages, updatePagesToRender } from 'actions/pages';
import { createEditorSession, otpResend, submitContactForm, submitOtpForm } from 'actions/editor';
import { fetchGating, resetGating } from 'actions/gating';
import { fetchMappingsForContentGrid, updateFiltersInContenGrid, resetFiltersInContenGrid, updateQueryInContenGrid } from 'actions/contentGrid';
import { fetchPreviewForAsset } from 'actions/content';
import { fetchCustomFields } from 'actions/fields/fields';
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import { fetchCampaignsSegments, fetchPersonalizationLead } from 'actions/personalization';
import InfiniteScroll from 'components/InfiniteScroll';

import { LIVE_ELEMENT, PAGE, SLIDE } from 'constants/widgetTypes';

import _ from 'lodash';
import cx from 'classnames';
import shortid from 'shortid';
import { ParallaxProvider } from "react-scroll-parallax";

import { Route, Switch, useRouteMatch, useParams, useHistory, useLocation } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWindowClose } from '@fortawesome/free-solid-svg-icons';

import { Button, ButtonGroup, OverlayTrigger, Popover, Dropdown, Tab, Nav, InputGroup, Form } from 'react-bootstrap';
import Device from 'components/Device';
import {
  InteractiveContent, LandingPage, Widgets, SpatialContent,
  BrandThemeManager,
  Fonts, GatingManager
} from '@paperflite-marketing/cleverstory-commons';
import { LoadingMessage } from "pages/storyboards/storyboardSettings/leadReports/components";

import { registerEditorSessionReducer, useEditorSession } from 'components/Editor/hooks/useEditorSession';
import { useMediaQuery } from 'react-responsive';

import { getBaseUrl } from 'services/util';

import '@paperflite-marketing/asset-viewer/dist/index.css';
import 'video.js/dist/video-js.min.css';
import 'react-horizontal-scrolling-menu/dist/styles.css';
import styles from './preview.module.scss';
import { getPageViewedEventPayload } from 'services/mixpanelUtil';
import { MIXPANEL_PAGE_VIEWED, MIXPANEL_PAGE_VIEWED_PREVIEW } from 'constants/mixpanelEvents';
import { createMixpanelEvent } from 'actions/mixpanel';

const production = process.env.REACT_APP_DEVELOPMENT === 'false';
const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;

export function PreviewPage() {
  const dispatch = useDispatch();
  const history  = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const personalizationId = searchParams.get('p');
  const segmentId  = searchParams.get('s');
  
  let { path, url } = useRouteMatch();
  let { id } = useParams();
  const iframe = document.getElementById("cs-preview-iframe");
  const [expanded, setExpanded] = useState(false);
  const [embedUrl, setEmbedUrl] = useState(`${url}/embedded`);
  const page = useSelector((state) => _.get(state, `pages.page.${id}.item`));

  const storyboard = _.get(page, 'storyboard', null);
  const storyboardId = _.get(storyboard, 'id', '');
  const storyboardType = _.get(storyboard, 'type', '');
  const [device, setDevice] = useState('none');
  const [state, setState] = useState({
    gatingConfigs: [],
    configuration: null,
    loading: false
  })  

  const [icons, setIcons] = useState({
    primary: true,
    secondary: true
  })

  const handleDeviceLayout = (type) => {
    setDevice(type)
  }

  useEffect(() => {
    if (personalizationId){
      setEmbedUrl(`${url}/embedded?p=${personalizationId}`);
    }
    else if (segmentId){
      setEmbedUrl(`${url}/embedded?s=${segmentId}`);
    }
    else{
      setEmbedUrl(`${url}/embedded`);
    }

    dispatch(fetchPage(id, true, personalizationId, segmentId));
  }, [id, personalizationId, segmentId]);

  useEffect(() => {
    if (storyboardId) {
      const request ={
        query: "",
        storyboard: storyboardId,
        sort: "modified_date",
        order: "DESC",
      }
      dispatch(fetchPersonalizationLead(request, 0, 10));
      dispatch(fetchCampaignsSegments(request, 0, 10));
    }
  }, [storyboard]);

  useEffect(()=>{
    if(device == 'none' || device == 'tabletls') {
      setIcons((state)=> {
        return {
          primary: device === 'none',
          secondary: true
        }
      })
      return;
    }
    setIcons((state)=> {
      return {
        primary: true,
        secondary: device === 'mobile' 
      }
    })
  },[device])

  const handleMessage = (e) => {
    switch (e.data.type) {
      case 'cleverstory:gating:configs': {
        var gatingConfigs = _.get(e, 'data.payload.gatingConfigs', []);
        var loading = _.get(e, 'data.payload.loading', false);

        const newGatingConfigs = _.uniqBy([...gatingConfigs, ...state.gatingConfigs], 'id');
        setState(state => {
          return {
            ...state,
            gatingConfigs: newGatingConfigs,
            loading: loading
          }
        });

        break;
      }

      case 'cleverstory:gating:closed': {
        var switchGating = _.get(e, 'data.payload.switchGating', false);
        if (switchGating){
          return;
        }
        setState(state => {
          return {
            ...state,
            configuration: null
          }
        });

        break;
      }
    }
  }

  const handleSelectGating = (gatingConfig) => {
    if (!_.isEqual(state.configuration, gatingConfig)){
      handleCloseGating(true);
    }
    if (iframe) {
      iframe.contentWindow.postMessage({
        type: 'cleverstory:gating:preview',
        payload: {
          page: id,
          configuration: gatingConfig
        }
      }, '*');

      setState(state => {
        return {
          ...state,
          configuration: gatingConfig
        }
      })
    }
  }

  const handleCloseGating = (switchGating) => {
    if (iframe) {
      iframe.contentWindow.postMessage({
        type: 'cleverstory:gating:closed',
        payload: {
          page: id,
          configuration: state.configuration,
          switchGating: switchGating
        }
      }, '*');

      setState(state => {
        return {
          ...state,
          configuration: null
        }
      })
    }
  }

  useEffect(() => {
    window.addEventListener('message', handleMessage, false);

    return () => {
      window.removeEventListener('message', handleMessage);
    }
  }, [])

  return (
    <>
    <div className={styles.previewHeader} >
      <div className={styles.expandText} {...(!expanded && {'data-track-event': 'Mixpanel', 'data-track-props': 'Expand Button'})} onClick={() => setExpanded(!expanded)}>
        <span>{expanded ? 'Collapse' : 'Expand'}</span>
        <i className='cleverstory-collapse' />
      </div>
      {!['SPATIAL', 'INTERACTIVE_CONTENT'].includes(storyboardType) &&     
        <div className={styles.home}>
          <LandingPageDropdown storyboard={storyboard} />
        </div>
      }
    </div>
    <PersonalizationLeadDetails storyboard={storyboard} expanded={expanded} setExpanded={setExpanded} personalizationId={personalizationId} segmentId={segmentId}/>
    <div className={styles.preview}>
      <Device className={cx(styles.devices, styles[device], {[styles.leadDetailsPreview] : expanded})} mode={device}>
        <iframe key={device} id='cs-preview-iframe' src={embedUrl}></iframe>
      </Device>

      <ButtonGroup className={styles.devicesSwitcher}>
        {state.configuration &&
          <Button className={styles.closeGating} onClick={() => handleCloseGating(false)}>
            <i className={cx("cleverstory-close", styles.icon)} />
          </Button>
        }
        {state.gatingConfigs && state.gatingConfigs.length > 0 &&
          <Dropdown className={styles.gatingButtonDropdown}>
            <Dropdown.Toggle variant="Secondary" id="dropdown-basic">
              <i className={cx("cleverstory-lock", styles.icon)} />
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <Dropdown.Header>
                <h4 className={styles.header}>PREVIEW GATING</h4>
              </Dropdown.Header>
              {state.gatingConfigs.map((item, index) => {
                let gating = _.get(item, 'gating', null);
                if(!gating) {
                  return;
                }
                return <Dropdown.Item key={index} onClick={() => handleSelectGating(item)}>{gating.name}</Dropdown.Item>
              })}
              {state.loading && <div className={styles.loader}>Loading...</div>}
            </Dropdown.Menu>
          </Dropdown>
        }
        <span className={styles.layoutOptions}>
          <Button id='primary' className={cx({ [styles.active]: (device == 'none' || device == 'tabletls') })} 
            onClick={()=>handleDeviceLayout(device === 'none' ? 'tabletls' : 'none')}
            onMouseEnter={()=> {
              if(device == 'none' || device == 'tabletls'){
              setIcons((state)=>{
              return {
                ...state,
                primary: !state.primary 
              }})
            }}}
            onMouseLeave={()=> {
              if(device == 'none' || device == 'tabletls'){
                setIcons((state)=>{
                return {
                  ...state,
                  primary: device == 'none' 
                }})
              }}}
            >
            {icons.primary  && <i className={cx("cleverstory-laptop", styles.icon)} />}
            {!icons.primary  && <i className={cx("cleverstory-tablet-ls", styles.icon)} />}
          </Button>

          <Button id='secondary' className={cx({ [styles.active]: (device == 'mobile' || device == 'tablet') })} 
            onClick={()=>handleDeviceLayout(device === 'mobile' ? 'tablet' : 'mobile')}
            onMouseEnter={()=> {
              if(device == 'mobile' || device == 'tablet'){
              setIcons((state)=>{
              return {
                ...state,
                secondary: !state.secondary 
              }})
            }}}
            onMouseLeave={()=> {
              if(device == 'mobile' || device == 'tablet'){
                setIcons((state)=>{
                return {
                  ...state,
                  secondary: device == 'mobile' 
              }})
            }}}
            >
            {icons.secondary && <i className={cx("cleverstory-mobile", styles.icon)} /> }
            {!icons.secondary &&  <span className={styles.rotate}><i className={cx("cleverstory-tablet-ls", styles.icon)}/> </span>}
          </Button>
        </span>
      </ButtonGroup>
    </div>
    </>
  );
}

const PersonalizationLeadDetails = ({storyboard, expanded, setExpanded, personalizationId, segmentId}) => {
  const dispatch = useDispatch();
  const [value, setValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeState, setActiveState] = useState("people");
  const loading = useSelector((state) => _.get(state, `personalization.${activeState === "people" ? "leads" : "campaignsSegments"}.loading`, false));
  const leads = useSelector((state) => _.get(state, "personalization.leads.items", [])) || [];
  const campaigns = useSelector((state) => _.get(state, "personalization.campaignsSegments.items", [])) || [];
  const pagination = useSelector((state) => _.get(state, `personalization.${activeState === "people" ? "leads" : "campaignsSegments"}.pagination`, {}));
  const hasMoreItems = _.get(pagination, "currentPage") < _.get(pagination, "totalPages") - 1;


  const filteredCampaigns = useMemo(() => {
    return campaigns
      .map(campaign => {
        const filteredSegments = campaign.audienceSegmentResponse.filter(segment =>
          segment.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (filteredSegments.length === 0) return null;

        return {
          ...campaign,
          audienceSegmentResponse: filteredSegments,
        };
      })
      .filter(Boolean);
  }, [searchTerm, campaigns]);

  const loadMore = () => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }
    const request = {
      query: activeState === "people" ? searchTerm : '',
      storyboard: storyboard.id,
      sort: "modified_date",
      order: "DESC",
    };

    if (activeState === "people"){
      dispatch(fetchPersonalizationLead(request, nextPage, 10));
    }
    else{
      dispatch(fetchCampaignsSegments(request, nextPage, 10));
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchTerm(value);
    }, 300);

    return () => clearTimeout(handler);
  }, [value]);  

  useEffect(() => {
    if (!storyboard){
      return;
    }
    const request = {
      query: activeState === "people" ? searchTerm : '',
      storyboard: storyboard.id,
      sort: "modified_date",
      order: "DESC",
    };

    if (activeState === "people"){
      dispatch(fetchPersonalizationLead(request, 0, 10));
    }
  },[searchTerm, activeState]);

  useEffect(() => {
    setValue('');
  }, [activeState]);

  const handleManageActiveState = (active) => {
    setActiveState(active);
  }

  return(
    <div className={cx(styles.leadsContainer, {[styles.show] : expanded})}>
      <div className={styles.leadsHeader}>
        <span>Preview by</span>
        <i className={cx("cleverstory-close", styles.icon)} onClick={() => setExpanded(!expanded)}/>
      </div>
        <Tab.Pane eventKey="leads" className={cx(styles.leadTabPane)}>
          <div className={styles.leadsManagement}>
            <Tab.Container id="lead-management-tab" defaultActiveKey="people">
              <header className={styles.leadsManagementHeader}>
                <Nav variant="pills" className={styles.leadsTab}>
                  <Nav.Item>
                    <Nav.Link eventKey="people" onClick={() => handleManageActiveState("people")}>People</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="segments" onClick={() => handleManageActiveState("segments")}>Segments</Nav.Link>
                  </Nav.Item>
                </Nav>
              </header>
              <aside>
                <InputGroup className={styles.searchBox}>
                  <InputGroup.Text className={styles.searchIcon} id="basic-addon2">
                    <i className="cleverstory-search" />
                  </InputGroup.Text>
                  <Form.Control
                    className={styles.searchInput}
                    placeholder="Search"
                    aria-label="Search"
                    onChange={(e) => setValue(e.target.value)}
                    value={value}
                  />
                </InputGroup>
              </aside>
              <Tab.Content className={styles.leadsContentContainer}>
              {loading && ((leads && leads.length === 0) || (campaigns && campaigns.length === 0)) &&(
                <div className={cx(styles.loader, styles.modalLoader)}>
                  <LoadingMessage className={styles.loadingMessage} />
                </div>
              )}
              {!loading && leads && leads.length === 0 && activeState === 'people' &&
                <div className={styles.emptyMessage}> 
                  <span>No Leads Available</span>
                </div>
              }

              {!loading && filteredCampaigns && filteredCampaigns.length === 0 && activeState === 'segments' &&
                <div className={styles.emptyMessage}> 
                  <span>No Segments Available</span>
                </div>
              }
                <Tab.Pane eventKey="people">
                  {!loading && leads && leads.length > 0 && (
                    <InfiniteScroll
                      className={styles.list}
                      loading={loading}
                      hasMore={hasMoreItems}
                      loadMore={loadMore}
                      hideLoadMoreButton={true}
                    >
                      {leads.map((lead, index) => (
                        <LeadListRender
                          key={index}
                          lead={lead}
                          personalizationId={personalizationId}
                        />
                      ))}
                    </InfiniteScroll>
                  )}
                </Tab.Pane>

                <Tab.Pane eventKey="segments">
                  {!loading && filteredCampaigns && filteredCampaigns.length > 0 && 
                    <InfiniteScroll
                      className={styles.list}
                      loading={loading}
                      hasMore={hasMoreItems}
                      loadMore={loadMore}
                      hideLoadMoreButton={true}
                    >
                      {filteredCampaigns.map((campaign, idx) => (
                        <React.Fragment key={idx}>
                          <div className={styles.campaignWrapper}>
                            <TruncatedTextWithTooltip content={campaign.name.toUpperCase()} className={styles.campaignTitle} maxWidth={200} placement='top' />
                            {campaign.audienceSegmentResponse.map((segment, sIdx) => (
                              <SegmentListRender
                                key={`${idx}-${sIdx}`}
                                segment={segment}
                                segmentId={segmentId}
                              />
                            ))}
                          </div>
                        </React.Fragment>
                      ))}
                   </InfiniteScroll>
                  }
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </div>
        </Tab.Pane>
    </div>
  )
}

const LeadListRender = React.memo(({ lead, key, personalizationId}) => {
  const history  = useHistory();
  let { url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);

  const { id } = useParams();
  const leadContact = _.get(lead, "contact", {});

  const handleActiveLead = (lead) => {
    history.push(`${baseUrl}/pages/${id}/preview?p=${lead.id}`);
  }

  const renderInitials = (name) => {
    let firstName = _.get(name, "firstName") || "-";
    let lastName = _.get(name, "lastName") || "";

    let initial = "";

    if (name) {
      initial = firstName.charAt(0);
    }

    if (lastName) {
      initial = initial + lastName.charAt(0);
    }

    return initial.toUpperCase();
  };


  return (
    <div key={key} data-track-event="Mixpanel" data-track-props="List of Leads" 
      onClick={()=>{handleActiveLead(lead)}} className={cx(styles.leadContainer, {[styles.active]: _.isEqual(personalizationId, lead.id)})}>
      <div className={styles.details}>
        <div className={styles.leadInitials}>{renderInitials(leadContact)}</div>
        <div className={styles.data}>
          <TruncatedTextWithTooltip content={`${leadContact.firstName} ${leadContact.lastName}`} className={styles.name} maxWidth={200} placement='top' />
          <TruncatedTextWithTooltip content={leadContact.email} className={styles.mail} maxWidth={200} placement='top' />
        </div>
      </div>
    </div>
  );
});

const SegmentListRender = React.memo(({ segment, segmentId }) => {
  const history  = useHistory();
  let { url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);

  const { id } = useParams();

  const handleSegment = (segment) => {
    history.push(`${baseUrl}/pages/${id}/preview?s=${segment.id}`);
  }

  return (
    <div className={cx(styles.segmentContainer, {[styles.active]: _.isEqual(segmentId, segment.id)})} onClick={() => handleSegment(segment)}>
      <TruncatedTextWithTooltip content={segment.name} className={styles.segmentItem} maxWidth={200} placement='top' />
    </div>
  );
});

const LandingPageDropdown = ({ storyboard }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  let { url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);

  const { id } = useParams();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const pages = useSelector(state => _.get(state, 'pages.items', []));
  const fetched = useSelector(state => _.get(state, 'pages.fetched', false));

  const currentPage = pages.find(page => page.id === id)
  const hidden = _.get(currentPage, 'hidden', false)

  const homePage = _.get(storyboard, 'page', null);

  const isHomePage = (id) => {
    return homePage && homePage.id === id;
  }

  useEffect(() => {
    if (storyboard && !fetched) {
      handleSearchPages();
    }
  }, [storyboard]);

  const handleSearchPages = () => {
    var request = {
      storyboard: storyboard.id
    }

    dispatch(searchPages(request, 0, 50));
  }

  const handleChange = (page) => {
    history.push(`${baseUrl}/pages/${page.id}/preview`);
  }

  const _renderTitle = () => {
      var activePage = pages.find(item => item.id === id);
      return _.get(activePage, 'name', '');
  }

  return (
    <Dropdown className={styles.pageDropDown} onToggle={(isOpen) => setIsDropdownOpen(isOpen)}
      {...(!isDropdownOpen && { 'data-track-event': 'Mixpanel', 'data-track-props': 'Page List Dropdown' })}>
      <Dropdown.Toggle>
        <span className={styles.name}>
          {(isHomePage(id) && _.get(storyboard, 'type')  !== 'SPATIAL') && <i className={cx('cleverstory-home', styles.homeIcon)}></i>}
          <TruncatedTextWithTooltip placement="bottom" maxWidth={180} content={_renderTitle() || 'Loading...'} />
          {hidden && <i className={cx('cleverstory-hide', styles.iconHide)}></i>}
        </span>
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {pages && pages.map((page) => {
          const active = page.id === id;
          return (
            <Dropdown.Item active={active} key={page.id} onClick={() => handleChange(page)}>
              <span className={styles.name}>
                {(isHomePage(page.id) &&  _.get(storyboard, 'type')  !== 'SPATIAL') && <i className={cx('cleverstory-home', styles.homeIcon)}></i>}
                <TruncatedTextWithTooltip placement="bottom" content={page.name} />
                {page.hidden && <i className={cx('cleverstory-hide', styles.iconHide)}></i>}
              </span>
            </Dropdown.Item>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
}

export function EmbeddedPageView(props) {
  const dispatch = useDispatch();

  const { id } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const personalizationId = searchParams.get('p');
  const segmentId = searchParams.get('s');

  const editorRef = React.useRef(`editors_${id}`);

  const page = useSelector((state) => _.get(state, `pages.page.${id}.item`));
  const pageId = _.get(page, "id");
  const pageLoaded = _.get(page, "id") != null;

  const storyboard = _.get(page, 'storyboard', null);
  const storyboardId = _.get(storyboard, 'id', '');
  const storyboardName = _.get(storyboard, 'name', '');
  const storyboardType = _.get(storyboard, 'type', '');
  const appearance = _.get(storyboard, 'appearance', null);

  useEffect(() => {
    if(pageLoaded){
      var mixPanelPayload = getPageViewedEventPayload(MIXPANEL_PAGE_VIEWED_PREVIEW, storyboardId, storyboardName, storyboardType)
      dispatch(createMixpanelEvent(MIXPANEL_PAGE_VIEWED, mixPanelPayload))
    }
  }, [pageLoaded]);

  useEffect(() => {
    editorRef.current = `editors_${id}`;
    dispatch(fetchPage(id, true, personalizationId, segmentId));
  }, [id, personalizationId, segmentId]);

  if(!pageLoaded) {
    return null;
  }

  return storyboardType === 'SPATIAL' ? 
    <SpatialPageView 
      appearance={appearance} 
      pageId={pageId} 
      storyboard={storyboard}
    /> : 
    <PageView 
      pageId={pageId} 
      storyboard={storyboard}
      personalizationId={personalizationId}
      segmentId={segmentId}
    />
}

export const PageView = ({ pageSummary, pageId, storyboard, spatialGatingConfigurations, personalizationId, segmentId, children }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const editorRef = React.useRef(`editors_${shortid()}`);
  const editorSession = useEditorSession(editorRef.current);
  const gatingContext = React.useContext(Widgets.GatingContext);
  const gatingRef = React.useRef(null);

  const [widgetContextState, setWidgetContextState] = useState({
    selector: '',
  });
  const [selectedDomain, setSelectedDomain] = useState({})

  const [formContextState, setFormContextState] = useState({
    onFormSubmit: () => { },
    onOtpFormSubmit: () => { },
    onOtpResend: () => { }
  });

  const [windowHeight, setWindowHeight] = useState('100vh');

  const page = useSelector((state) => _.get(state, `pages.page.${pageId}.item`));
  const pageLoaded = _.get(page, "id") != null;
  const activePage = useSelector(state => _.get(state, `pages.spatial.activePage`, null));
  const appearance = _.get(storyboard, 'appearance', null);
  const storyboardType = _.get(storyboard, 'type', '');
  const gatingConfigurationsList = useSelector(state => _.get(state, `pages.page.${pageId}.item.gatings`, []));
  const gatingConfigurations = storyboardType === 'SPATIAL' ? spatialGatingConfigurations : gatingConfigurationsList; 
  const gatings = useSelector(state => _.get(state, `gatings.gating`, []));
  var domain = useSelector(state => _.get(state, 'workspaces.workspace.item.defaultDomain') || "");

  const pageWidgets = useSelector((state) => {
    const widgets = _.get(state, `${widgetContextState.selector}.widgets`) || [];
    var pageSections = widgets.filter(item => {
      return item.type === PAGE;
    });

    if (storyboardType === 'INTERACTIVE_CONTENT'){
      pageSections = pageSections.filter((item) => _.get(item, "properties.desktop.visible") === true );
    }

    pageSections = _.sortBy(pageSections, 'order');

    return pageSections;
  });

  const fonts = useSelector(state => _.get(state, `pages.page.${pageId}.item.draft.fonts`) || []);
  Fonts.useFont(fonts);

  const desktop = useMediaQuery({
    query: '(min-width: 1024px)'
  });

  useEffect(() => {
    if (editorRef.current && pageLoaded) {
      const widgets = _.get(page, "draft.widgets") || [];

      var activePage = null;
      var pageSections = widgets.filter(item => {
        return item.type === PAGE;
      });

      pageSections = _.sortBy(pageSections, 'order');

      if (_.size(pageSections) > 0) {
        activePage = pageSections[0].uid;
      }

      const initialState = {
        widgets: widgets,
        interactive: {
          active: activePage
        }
      }

      dispatch(createEditorSession(editorRef.current, initialState));

      setWidgetContextState({
        session: editorRef.current,
        selector: `${editorRef.current}.present`,
        breakpoint: desktop ? 'desktop' : 'mobile',
        imageCDN: IMAGE_CDN_ENDPOINT,
        shouldZoom: true,
        preview: true,
        development: false,
        domain: _.get(selectedDomain, 'url'),
        createLiveElementContext: (element) => {
          const sessionId = `editors_live_element_${element.uid}`;

          return {
            ...widgetContextState,
            session: sessionId,
            selector: `${sessionId}.present`
          }
        },
        createGatingContext: (gating) => {
          const sessionId = `editors_gating_${gating}`;

          return {
            ...widgetContextState,
            session: sessionId,
            selector: `${sessionId}.present`
          }
        },
        fetchGating: (gating) => {
          dispatch(fetchGating(gating, true))
        },
        fetchPage: (id) => {
          dispatch(fetchPage(id, true, personalizationId, segmentId));
        },
        updateCarouselSlide: (payload) => {
          setWidgetContextState(prev => ({
            ...prev,
            carouselSlide: payload
          }));
        }
      });

      setFormContextState({
        onFormSubmit: handleFormSubmit,
        onOtpFormSubmit: handleOtpFormSubmit,
        onOtpResend: handleOtpResend
      });

      widgets.filter(item => item.type === LIVE_ELEMENT).forEach(item => {
        const sessionId = `editors_live_element_${item.uid}`;
        registerEditorSessionReducer(sessionId);
        dispatch(createEditorSession(sessionId, {
          widgets: item.children
        }));
      });
    }
  }, [editorRef.current, pageLoaded]);

  useEffect(() => {
    setWidgetContextState((state) => {
      return {
        ...state,
        domain: _.get(selectedDomain, 'url'),
        breakpoint: desktop ? 'desktop' : 'mobile'
      }
    });
  }, [desktop, selectedDomain]);

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    handleWindowResize();

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    }
  }, []);

  useEffect(() => {
    if (storyboard) {
      setWidgetContextState((state) => {
        return {
          ...state,
          domain: _.get(selectedDomain, 'url'),
          storyboard: storyboard
        }
      });

      dispatch(fetchCustomFields(_.get(storyboard, 'id')))
    }

    let domainUrl = _.get(storyboard, 'domain') || domain;
    setSelectedDomain(domainUrl);
  }, [storyboard]);

  useEffect(() => {

    if (gatingConfigurations && gatingConfigurations.length > 0) {

      gatingConfigurations.forEach(item => {
        let gating = _.get(item, 'gating.id', null);
        if (gating && !gatings?.[gating]) {
          dispatch(fetchGating(gating, true))
        }
      })

      window.parent.postMessage({
        type: "cleverstory:gating:configs",
        payload: {
          page: pageId,
          gatingConfigs: gatingConfigurations,
          loading: !pageLoaded
        }
      }, '*');
    }
  }, [gatingConfigurations, pageLoaded]);

  useEffect(() => {
    window.addEventListener('message', handleMessage, false);

    return () => {
      window.removeEventListener('message', handleMessage);
    }
  }, []);

  const handleChange = (type, data) => {
    if (type === 'index') {
      //console.log("Current page -" + data.index);
    } else if (type === 'progress') {
      //console.log(`Page ${data.index} - progress ${data.progress}`);
    }
  }

  const handleFormSubmit = (editorSession, formId, fields) => {
    dispatch(submitContactForm(editorSession, formId));
  }

  const handleOtpFormSubmit = (editorSession, formId, fields) => {
    dispatch(submitOtpForm(editorSession, formId));
  }

  const handleOtpResend = (editorSession, formId, fields) => {
    dispatch(otpResend(editorSession, formId));
  }

  const handleWindowResize = () => {
    const height = window.innerHeight;
    setWindowHeight(`${height}px`);
  }

  const handleResetGating = (page, switchGating) => {
    window.parent.postMessage({
      type: "cleverstory:gating:closed",
      payload: {
        page: pageId,
        switchGating: switchGating
      }
    }, '*');
  }

  const handleMessage = (e) => {
    switch (e.data.type) {
      case 'cleverstory:gating:preview': {
        var configuration = _.get(e, 'data.payload.configuration', null);
        if (configuration) {
          gatingRef.current.triggerGating('PREVIEW', null, configuration);
        }

        break;
      }

      case 'cleverstory:gating:closed': {
        var configuration = _.get(e, 'data.payload.configuration', null);
        var switchGating = _.get(e, 'data.payload.switchGating', false);
        gatingRef.current.closeGating(configuration, switchGating);

        break;
      }
    }
  }

  return (
    <div key={widgetContextState.breakpoint} className={styles.content}>
      <style>
        {`
          :root{
            --window-height: ${windowHeight};
          }
        `}
      </style>

      <BrandThemeManager className={".cs_text_widget"} themes={_.get(storyboard, 'themes')} />

      <Widgets.Context.Provider value={widgetContextState}>
        <Widgets.FormContext.Provider value={formContextState}>
          <GatingManager ref={gatingRef}
            configurations={gatingConfigurations}
            appearance={appearance}
            storyboardType={storyboardType}
            resetGating={handleResetGating}
            page={pageId}
            breakpoint={widgetContextState.breakpoint}
            preview={true}>
            <Widgets.Content.Manager
              debug={!production}
              fetchMappingsForContentGrid={(contentGrid, page, limit) => {
                dispatch(fetchMappingsForContentGrid(editorSession.id, contentGrid, page, limit, true));
              }}
              fetchPreviewDataForAsset={(asset) => {
                dispatch(fetchPreviewForAsset(asset));
              }}
              updateFiltersInContenGrid={(contentGrid, filters) => {
                dispatch(updateFiltersInContenGrid(editorSession.id, contentGrid, filters));
              }}
              updateQueryInContenGrid={(contentGrid, query) => {
                dispatch(updateQueryInContenGrid(editorSession.id, contentGrid, query));
              }}
              resetFiltersInContenGrid={(contentGrid) => {
                dispatch(resetFiltersInContenGrid(editorSession.id, contentGrid));
              }}
              onContentChange={(content) => {
                const url = _.get(content, 'content.alias');
                const name = _.get(content, 'content.name');
                window.history.replaceState(null, name, url);
              }}
              onAnalyticsChange={(asset, analytics) => {

              }}
              onDownload={(asset) => {
                console.log(`Download Asset ${asset.id}`);
              }}
              onClose={() => {
                console.log("Close Dialog");
              }}
            >
              <Widgets.Actions.Provider value={{
                onBeforeExecution: () => { }
              }}>
                {storyboardType === 'INTERACTIVE_CONTENT' &&
                  <InteractiveContent initialPage={0} appearance={appearance} onChange={handleChange}>
                    {pageWidgets.map((widget, index) => {
                      return <Widgets.Widget key={widget.uid} widgetProps={widget} breakpoint={widgetContextState.breakpoint} />
                    })}
                  </InteractiveContent>
                }

                {["LANDING_PAGE", "RESOURCE_CENTER"].includes(storyboardType) &&
                  <LandingPage onChange={handleChange} pageWidgets={pageWidgets}>
                    {(_.orderBy(pageWidgets, 'order', 'desc')).map((widget, index) => {
                      return <Widgets.Widget key={widget.uid} widgetProps={widget} breakpoint={widgetContextState.breakpoint} />
                    })}
                  </LandingPage>
                }

                {storyboardType === 'SPATIAL' && 
                  <SpatialPageRender appearance={appearance} page={page ? page : pageSummary} pageId={pageId} />
                }
              </Widgets.Actions.Provider>
            </Widgets.Content.Manager>
          </GatingManager >
        </Widgets.FormContext.Provider>
      </Widgets.Context.Provider>
    </div>
  );
}

const PAGES_TO_LOAD = 4;

export const SpatialPageView = ({ pageId, appearance, storyboard }) => {
  const dispatch = useDispatch();

  let { url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);

  const pages = useSelector(state => _.get(state, `pages.spatial.items`, []));
  const pagesToRender = useSelector(state => _.get(state, `pages.spatial.pagesToRender`, []));
  const spatialGatingConfigurations = useFetchAllGatings(pages);

  const desktop = useMediaQuery({
    query: '(min-width: 1024px)'
  });


  const handleChange = (type, data) => {
    if (type === 'index') {
      //console.log("Current page -" + data.index);
    } else if (type === 'progress') {
      //console.log(`Page ${data.index} - progress ${data.progress}`);
    }

    const id = _.get(data, 'activePage.id', null);
    if(id) {
      const currentPage = _.find(pages, { id: id })
      window.history.replaceState(null, currentPage.name, `${baseUrl}/pages/${currentPage.id}/preview/embedded`);
    }
  }

  const handleFetchPage = (page) => {
    dispatch(fetchPage(page, true));
  }

  const handleUpdatePagesToRender = (pages, activePage) => {
    dispatch(updatePagesToRender(pages, activePage));
  }

  return (
    <SpatialContent appearance={appearance}  pages={pages} 
      imageCDN={IMAGE_CDN_ENDPOINT}
      breakpoint={desktop ? 'desktop' : 'mobile'} 
      initialPage={pageId} 
      onChange={handleChange}
      onFetchPage={handleFetchPage}
      updatePagesToRender={handleUpdatePagesToRender}
      pagesToRender={pagesToRender}
    >
      {pages &&
        pages.map((page, i) => {
          return <PageView key={i} pageId={page.id} pageSummary={page} storyboard={storyboard} spatialGatingConfigurations={spatialGatingConfigurations}/>
        })
      }
    </SpatialContent>
  )
}

const SpatialPageRender = ({appearance, page }) => {

  const widgetContext = React.useContext(Widgets.Context);

  const slideWidgets = useSelector((state) => {
    const widgets = _.get(state, `${widgetContext.selector}.widgets`) || [];
    var slides = widgets.filter(item => {
      return item.type === SLIDE;
    });

    slides = _.sortBy(slides, 'order');

    return slides;
  });
  
  const pagesToRender = useSelector(state => _.get(state, `pages.spatial.pagesToRender`, []));
  const loading = useSelector(state => _.get(state, `pages.page[${page.id}].loading`, false));
  const renderPage = pagesToRender.some(id => id === page.id);

  return (
    <ParallaxProvider>
      <SpatialContent.SpatialPage preview={true} appearance={appearance} page={page} slideWidgets={slideWidgets} loading={loading}>
        {renderPage && slideWidgets.map((widget, index) => {
          return (
            <Widgets.Widget key={widget.uid} widgetProps={widget} breakpoint={widgetContext.breakpoint} />
          )
        })}
      </SpatialContent.SpatialPage>
    </ParallaxProvider>
  )
}

function useFetchAllGatings(pages) {
  const allGatings = useSelector(state =>
    pages?.map(page => _.get(state, `pages.page.${page.id}.item.gatings`, [])) || []
  );

  return allGatings.flat(); 
}
